import React from 'react';
import { useStore } from 'effector-react';
import { type HSIconMark, HSIconType, type HSPos } from '@types';
import Slider from '@components/Slider';
import Button from '@components/Button';
import WidgetItem from '@components/PlayerWidgets/WidgetItem';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import Tabbar from '@components/Tabbar';
import ColorPicker from '@components/ColorPicker';
import IconUp from '../../panel/PanelHotspot/IconSetting/IconSettingMark/IconUp';
import IconDown from '../../panel/PanelHotspot/IconSetting/IconSettingMark/IconDown';
import IconRight from '../../panel/PanelHotspot/IconSetting/IconSettingMark/IconRight';
import IconLeft from '../../panel/PanelHotspot/IconSetting/IconSettingMark/IconLeft';
import WidgetRow from './WidgetRow';
import hotspotIconDefault from '../utils/hotspotIconDefault';

const edgeValues: HSPos[] = ['bottom', 'top', 'right', 'left'];
const getIndex = (pos: HSPos) => edgeValues.findIndex((x) => x === pos) ?? 0;

const defaultData = hotspotIconDefault({ type: HSIconType.mark }) as HSIconMark;

export function MarkWidget() {
  const { hotspot } = useStore($editingHotspot)!;
  const icon = hotspot.icon as HSIconMark;
  const isTriangle = icon.lineType === 1;

  const tabs = [
    <IconUp active={icon.edge === 'bottom'} />,
    <IconDown active={icon.edge === 'top'} />,
    <IconRight active={icon.edge === 'right'} />,
    <IconLeft active={icon.edge === 'left'} />,
  ];

  const handleReset = () => {
    $editingHotspotApi.update((draft) => {
      const iconData = draft.icon as HSIconMark;
      iconData.length = defaultData.length;
      iconData.lineWidth = defaultData.lineWidth;
      iconData.lineColor = defaultData.lineColor;
      iconData.edge = defaultData.edge;
    });
  };

  return (
    <WidgetItem type="mark" title="标记线高级设置">
      <WidgetRow>
        {!isTriangle && (
          <Slider
            className="mb-20"
            value={icon.length}
            min={0}
            max={600}
            valToString={(val) => `${val}px`}
            onChange={(val: number) => {
              $editingHotspotApi.update((draft) => {
                (draft.icon as HSIconMark).length = val;
              });
            }}
          >
            <Slider.Header title="标记线长度：" />
            <Slider.Bar />
            <Slider.Control />
          </Slider>
        )}
        {!isTriangle && (
          <Slider
            className="mb-20"
            value={icon.lineWidth ?? defaultData.lineWidth}
            min={1}
            max={10}
            valToString={(val) => `${val}px`}
            onChange={(val: number) => {
              $editingHotspotApi.update((draft) => {
                (draft.icon as HSIconMark).lineWidth = val;
              });
            }}
          >
            <Slider.Header title="标记线粗细：" />
            <Slider.Bar />
            <Slider.Control />
          </Slider>
        )}
        <ColorPicker
          colorString={icon.lineColor ?? defaultData.lineColor}
          onChange={(colorString) => {
            $editingHotspotApi.update((draft) => {
              (draft.icon as HSIconMark).lineColor = colorString;
            });
          }}
        >
          <ColorPicker.Header>标记线颜色：</ColorPicker.Header>
          <ColorPicker.Bar />
        </ColorPicker>
        <div className="mt-20 mb-10">标记线位置：</div>
        <Tabbar
          tabs={tabs}
          activeIndex={getIndex(icon.edge)}
          onChange={(index: number) => {
            const edge = edgeValues[index];
            $editingHotspotApi.update((draft) => {
              (draft.icon as HSIconMark).edge = edge;
            });
          }}
        />
      </WidgetRow>
      <Button className="w-full" onClick={handleReset}>
        重置设置
      </Button>
    </WidgetItem>
  );
}
